import { registerIndicator, type IndicatorDrawParams, type KLineData } from 'klinecharts'
import { registerOnce } from './registrationUtils'
import { isRegularSession } from './marketHours'

/**
 * Previous-day High / Low reference lines — the intraday sibling of
 * `previousClose.ts`. Same self-contained approach: derive the level from the
 * visible intraday `kLineDataList` (no daily datafeed), grouping bars by session
 * day and mapping each day to the PRIOR day's high (or low). Drawn as a dashed
 * horizontal segment spanning each day's bars, coloured from `extendData`.
 */
export interface PrevDayLevelConfig {
  color: string
  timezone: string
}

type Mode = 'high' | 'low'

const PREVIOUS_DAY_HIGH_NAME = 'PREVIOUS_DAY_HIGH'
const PREVIOUS_DAY_LOW_NAME = 'PREVIOUS_DAY_LOW'

// Cache DateTimeFormat per timezone (mirrors previousClose.ts).
const dtfCache = new Map<string, Intl.DateTimeFormat>()

function getDateStr(timestamp: number, tz: string): string {
  let dtf = dtfCache.get(tz)
  if (!dtf) {
    dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    dtfCache.set(tz, dtf)
  }
  return dtf.format(new Date(timestamp))
}

/**
 * Per-session REGULAR-HOURS extreme for each day: max(high) for 'high', min(low) for 'low',
 * over bars within 09:30-16:00 ET only (pre-market / after-hours excluded). Falls back to all
 * of a day's bars when it has no RTH bar (daily bars, whose high/low is already the RTH
 * extreme). Returned in chronological day order.
 */
export function buildDayExtremes(
  kLineDataList: KLineData[],
  tz: string,
  mode: Mode,
): { date: string; value: number }[] {
  const byDay = new Map<string, KLineData[]>()
  for (const bar of kLineDataList) {
    if (!bar) continue
    const d = getDateStr(bar.timestamp, tz)
    const arr = byDay.get(d)
    if (arr) arr.push(bar)
    else byDay.set(d, [bar])
  }

  const out: { date: string; value: number }[] = []
  for (const [d, dayBars] of byDay) {
    const rth = dayBars.filter((b) => isRegularSession(b.timestamp))
    const src = rth.length > 0 ? rth : dayBars
    let acc: number | undefined
    for (const b of src) {
      const level = mode === 'high' ? b.high : b.low
      acc = acc == null ? level : mode === 'high' ? Math.max(acc, level) : Math.min(acc, level)
    }
    if (acc != null) out.push({ date: d, value: acc })
  }
  return out
}

export function makeDraw(mode: Mode) {
  return (params: IndicatorDrawParams): boolean => {
    const { ctx, kLineDataList, visibleRange, xAxis, yAxis, indicator } = params
    const config = indicator.extendData as PrevDayLevelConfig | undefined
    if (!config) return false

    const tz = config.timezone || 'America/New_York'
    const { from, to } = visibleRange
    if (from >= to || kLineDataList.length === 0) return false

    // Scan the whole series so a day at the left edge still sees its prior day.
    const dayExtremes = buildDayExtremes(kLineDataList, tz, mode)
    if (dayExtremes.length < 2) return false

    // date → PREVIOUS day's extreme.
    const prevByDate = new Map<string, number>()
    for (let i = 1; i < dayExtremes.length; i++) {
      prevByDate.set(dayExtremes[i].date, dayExtremes[i - 1].value)
    }

    ctx.save()
    ctx.strokeStyle = config.color
    ctx.lineWidth = 1
    ctx.setLineDash([4, 4])

    let currentDate = ''
    let dayStartIdx = from
    let dayValue: number | undefined

    for (let i = from; i <= to; i++) {
      const bar = i < to ? kLineDataList[i] : null
      const d = bar ? getDateStr(bar.timestamp, tz) : ''

      if (d !== currentDate || i === to) {
        if (currentDate && dayValue != null) {
          const x1 = xAxis.convertToPixel(dayStartIdx)
          const x2 = xAxis.convertToPixel(i - 1)
          const y = yAxis.convertToPixel(dayValue)
          if (isFinite(y) && isFinite(x1) && isFinite(x2)) {
            ctx.beginPath()
            ctx.moveTo(x1, y)
            ctx.lineTo(x2, y)
            ctx.stroke()
          }
        }
        currentDate = d
        dayStartIdx = i
        dayValue = prevByDate.get(d)
      }
    }

    ctx.restore()
    return false
  }
}

function registerLevel(name: string, mode: Mode): void {
  registerIndicator({
    name,
    shortName: '',
    figures: [],
    calc: (dataList: KLineData[]) => dataList.map(() => ({})),
    draw: makeDraw(mode),
  })
}

/** Register the previous-day high + low indicators with klinecharts (once). */
export const registerPrevDayHighLowIndicators = registerOnce(() => {
  registerLevel(PREVIOUS_DAY_HIGH_NAME, 'high')
  registerLevel(PREVIOUS_DAY_LOW_NAME, 'low')
})

export { PREVIOUS_DAY_HIGH_NAME, PREVIOUS_DAY_LOW_NAME }
